/**
 * 从 SQL 中提取数据源
 * 根据 SQL 中的表名判断数据源类型
 */

// 数据表到数据源的映射
const TABLE_TO_SOURCE_MAP: Record<string, string[]> = {
  // Shopify 表
  'semantic.dm_shop_info_shopify': ['Shopify'],
  'semantic.dm_orders_shopify': ['Shopify'],
  'semantic.dm_order_items_shopify': ['Shopify'],
  'semantic.dm_products_shopify': ['Shopify'],
  'semantic.dm_variants_shopify': ['Shopify'],
  'semantic.dm_customers_shopify': ['Shopify'],
  'semantic.dm_shopify_pixel_events': ['Shopify'],

  // Google Ads 表
  'semantic.dm_ads_campaigns_google': ['Google Ads'],
  'semantic.dm_ads_products_google': ['Google Ads'],
  'semantic.dm_ads_keywords_google': ['Google Ads'],
  'semantic.dm_ads_search_terms_google': ['Google Ads'],
  'semantic.dm_ads_creatives_google': ['Google Ads'],

  // BigCommerce 表
  'semantic.dm_shop_info_bigcommerce': ['BigCommerce'],
  'semantic.dm_products_bigcommerce': ['BigCommerce'],
  'semantic.dm_variants_bigcommerce': ['BigCommerce'],
  'semantic.dm_customers_bigcommerce': ['BigCommerce'],
  'semantic.dm_orders_bigcommerce': ['BigCommerce'],
  'semantic.dm_order_items_bigcommerce': ['BigCommerce'],
};

/**
 * 从 SQL 中提取表名
 * 支持多种 SQL 格式:
 * - SELECT * FROM table_name
 * - SELECT * FROM schema.table_name
 * - JOIN table_name
 * - FROM "table_name"
 * - FROM `table_name`
 */
const extractTableNames = (sql: string): string[] => {
  if (!sql || typeof sql !== 'string') return [];

  const tableNames: string[] = [];

  // 匹配 FROM 或 JOIN 后面的表名
  // 支持: FROM/JOIN table_name, FROM/JOIN schema.table_name, FROM/JOIN "table_name", FROM/JOIN `table_name`
  const patterns = [
    /(?:FROM|JOIN)\s+([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)/gi,
    /(?:FROM|JOIN)\s+"([^"]+)"/gi,
    /(?:FROM|JOIN)\s+`([^"]+)`/gi,
  ];

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(sql)) !== null) {
      const tableName = match[1];
      if (tableName && !tableNames.includes(tableName)) {
        tableNames.push(tableName);
      }
    }
  }

  return tableNames;
};

/**
 * 从表名列表中提取数据源
 */
const getDataSourcesFromTables = (tableNames: string[]): string[] => {
  const sources = new Set<string>();

  for (const tableName of tableNames) {
    // 直接匹配
    if (TABLE_TO_SOURCE_MAP[tableName]) {
      TABLE_TO_SOURCE_MAP[tableName].forEach(source => sources.add(source));
    }

    // 模糊匹配(处理带引号或不带引号的情况)
    const normalizedTableName = tableName.replace(/["`]/g, '').toLowerCase();
    for (const [mappedTable, mappedSources] of Object.entries(TABLE_TO_SOURCE_MAP)) {
      if (mappedTable.toLowerCase() === normalizedTableName) {
        mappedSources.forEach(source => sources.add(source));
      }
    }
  }

  return Array.from(sources);
};

/**
 * 从 SQL 中提取数据源
 * @param sql SQL 查询语句
 * @returns 数据源数组,如 ['Shopify', 'Google Ads']
 */
export const extractDataSources = (sql: string): string[] => {
  const tableNames = extractTableNames(sql);
  const sources = getDataSourcesFromTables(tableNames);
  return sources;
};

/**
 * 获取数据源的小写标识(用于图标匹配)
 * @param source 数据源名称,如 'Shopify', 'Google Ads'
 * @returns 小写的标识,如 'shopify', 'google'
 */
export const getSourceKey = (source: string): string => {
  const sourceMap: Record<string, string> = {
    'Shopify': 'shopify',
    'Google Ads': 'google ads',
    'Google Analytics': 'google analytics',
    'BigCommerce': 'bigcommerce',
    'Facebook Ads': 'facebook ads',
    'TikTok Ads': 'tiktok ads',
    'Amazon': 'amazon',
  };

  return sourceMap[source] || source.toLowerCase();
};
